import React from 'react';

import { useAuthState } from '@/utils/auth';
import { FaExclamationCircle } from '@react-icons/all-files/fa/FaExclamationCircle';
import { FaMobileAlt } from '@react-icons/all-files/fa/FaMobileAlt';
import { FaRegBell } from '@react-icons/all-files/fa/FaRegBell';
import { InstallPromptModal, useInstallPrompt } from '@/hooks/usePWAInstall';
import { usePushCapability } from '@/hooks/usePushCapability';
import { isNotNull } from '@/utils/nullish';
import { NotificationsPrompt } from '@/utils/NotificationsPrompt';
import { useWebPushStatus } from '@/utils/WebPushHandler';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useHistory } from 'react-router-dom';
import { cn } from '@/utils/utils';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';

const notificationSheetOpenAtom = atom(false);
const unreadNotificationCountAtom = atom(0);

export const useToggleNotificationSheet = () => {
  const setAtom = useSetAtom(notificationSheetOpenAtom);
  return () => setAtom((prev) => !prev);
};
export const useUnreadNotificationCount = () =>
  useAtomValue(unreadNotificationCountAtom);

const NotificationSheet: React.FC<{
  side: 'left' | 'right';
}> = ({ side }) => {
  const [open, setOpen] = useAtom(notificationSheetOpenAtom);
  const [showInstall, setShowInstall] = React.useState(false);
  const [showNotifPrompt, setShowNotifPrompt] = React.useState(false);
  const { canPrompt, shouldPrompt, acceptInstall, declineInstall } =
    useInstallPrompt();
  const { user } = useAuthState();
  const webPushStatus = useWebPushStatus();
  const capability = usePushCapability();
  const history = useHistory();

  // On iOS in a browser tab the install prompt is not a nicety, it is the
  // only route to notifications at all — so it must open even after the
  // generic 30-day install nag has been dismissed.
  const installModalPrompt: 'ios' | boolean =
    capability === 'needs-install' ? 'ios' : shouldPrompt;

  let notificationsData = React.useMemo(
    () => [
      ...(user
        ? user.invitations.filter(isNotNull).map((invitation) => ({
            id: invitation.id,
            Icon: FaExclamationCircle,
            title: 'Invitación',
            description: `Invitacion: ${invitation.label} en ${
              invitation.company.name
            }${invitation.project ? ' - ' + invitation.project.name : ''}`,
            date: 'Pendiente',
            seen: false,
            action: () =>
              history.push(`/confirm-employee?role_id=${invitation.id}`),
          }))
        : []),
      // Superseded by the notification-specific item below when push is the
      // reason to install — two "install this app" rows in one sheet is worse
      // than one that says why.
      ...(canPrompt && capability !== 'needs-install'
        ? [
            {
              id: 'install',
              Icon: FaMobileAlt,
              title: 'Instala esta app',
              description:
                'Instala esta app para recibir notificaciones y actualizaciones',
              seen: !shouldPrompt,
              action: () =>
                shouldPrompt === 'ios' ? setShowInstall(true) : acceptInstall(),
            },
          ]
        : []),
      ...(capability === 'needs-install'
        ? [
            {
              id: 'push-needs-install',
              Icon: FaMobileAlt,
              title: 'Instalá la app para recibir notificaciones',
              description:
                'En iPhone y iPad las notificaciones solo funcionan si agregás Bilderit a la pantalla de inicio',
              seen: false,
              action: () => setShowInstall(true),
            },
          ]
        : []),
      ...(webPushStatus === 'prompt-required'
        ? [
            {
              id: 'allow-notif',
              Icon: FaRegBell,
              title: 'Recibí notificaciones',
              description:
                'Activá las notificaciones para recibir las últimas novedades',
              seen: !shouldPrompt,
              action: () => setShowNotifPrompt(true),
            },
          ]
        : []),
    ],
    [
      acceptInstall,
      webPushStatus,
      canPrompt,
      capability,
      shouldPrompt,
      user,
      history,
    ]
  );

  return (
    <>
      <NotificationsPrompt
        shouldPrompt={showNotifPrompt}
        toggle={() => setShowNotifPrompt(false)}
      />
      <InstallPromptModal
        shouldPrompt={showInstall && installModalPrompt}
        acceptInstall={acceptInstall}
        declineInstall={declineInstall}
      />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side={side} className="w-[300px] sm:w-[400px]">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>Notificaciones</span>
              {/* <Button variant="ghost" size="sm">
                Marcar todas como leídas
              </Button> */}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {notificationsData.map((notification) => (
              <SheetTrigger key={notification.id} asChild>
                <div
                  className={cn(
                    `p-3 rounded-lg border`,
                    notification.seen
                      ? 'bg-card border'
                      : 'bg-muted border-muted-foreground'
                  )}
                  onClick={() => notification.action()}
                >
                  <div className="flex justify-between items-start">
                    <h3
                      className={cn(
                        'font-medium',
                        notification.seen && 'text-muted-foreground'
                      )}
                    >
                      {notification.title}
                    </h3>
                    {!notification.seen && (
                      <Badge className="bg-blue-500">Nueva</Badge>
                    )}
                  </div>
                  <p
                    className={cn(
                      'text-sm text-muted-foreground mt-1',
                      notification.seen ? '' : 'text-card-foreground'
                    )}
                  >
                    {notification.description}
                  </p>
                  {'date' in notification && (
                    <p className="text-xs text-gray-400 mt-2">
                      {notification.date}
                    </p>
                  )}
                </div>
              </SheetTrigger>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default NotificationSheet;
